import 'ol/ol.css';
import Map from 'ol/Map';
import { getCenter } from 'ol/extent';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Modify from 'ol/interaction/Modify';
import Select from 'ol/interaction/Select';
import Translate from 'ol/interaction/Translate';
import { never, platformModifierKeyOnly, primaryAction } from 'ol/events/condition';
import Style from 'ol/style/Style';
import Fill from 'ol/style/Fill';
import Stroke from 'ol/style/Stroke';
import Collection from 'ol/Collection';

// 유틸리티 클래스들 import (통합 관리)
import {
  ImageLoader,
  GeometryCalculator,
  GeoImageCreator,
  PolygonCreator,
  ImagePolygonSynchronizer,
  FixedCenterModificationHandler,
  InteractionSetupManager,
  StyleManager,
  PolygonTypeCalculator,
  TransformationTracker,
  TransformationApi,
  TransformationCalculator,
  TransformationUIHandler,
  ModifyHandler,
  TranslateHandler
} from '../../utils/imagePolygonManager/index.js';

// 이미지 좌표 계산 유틸리티 import
import imageCoordinateCalculator from '../../utils/imageCoordinateCalculator.js';

// 간결한 로깅 유틸리티
import { log } from '../../utils/logger.js';
import ContextMenu from 'ol-contextmenu';
import 'ol-contextmenu/dist/ol-contextmenu.css';
import { getVuetifyThemeVar } from '@/utils/mapLayerTheme';
import i18n from '@/plugins/i18n';

const getThemeVar = getVuetifyThemeVar;

const withOpacity = (color, opacity) => {
  if (!color) return color;
  if (color.startsWith('#')) {
    const hex = color.replace('#', '');
    const fullHex = hex.length === 3 ? hex.split('').map((ch) => ch + ch).join('') : hex;
    const r = parseInt(fullHex.slice(0, 2), 16);
    const g = parseInt(fullHex.slice(2, 4), 16);
    const b = parseInt(fullHex.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }
  if (color.startsWith('rgb(')) {
    return color.replace('rgb(', 'rgba(').replace(')', `, ${opacity})`);
  }
  return color;
};

/**
 * 이미지와 폴리곤을 한 쌍으로 관리하는 클래스 (리팩토링됨)
 */
export class ImagePolygonPair {
  constructor(id, imageUrl, openLayersMap, sharedVectorSource, coordinates, options = {}) {
    this.id = id;
    this.imageUrl = imageUrl;
    this.openLayersMap = openLayersMap;
    this.sharedVectorSource = sharedVectorSource;
    this.coordinates = coordinates;
    this.options = {
      opacity: 1.0,
      ...options
    };

    // 상태 관리
    this.isLoaded = false;
    this.isVisible = true;
    this.originalImage = null;
    this.currentPolygon = null;

    // GeoImage 관련
    this.geoImageLayer = null;
    this.geoImageSource = null;

    // 원본 폴리곤 geometry (변형 계산용)
    this.originalPolygonGeometry = null;
    this.originalPolygonSize = null;

    // 유틸리티 클래스들 초기화
    this.imageLoader = new ImageLoader();
    this.geometryCalculator = new GeometryCalculator();
    this.geoImageCreator = new GeoImageCreator();
    this.polygonCreator = new PolygonCreator();
    this.imagePolygonSynchronizer = new ImagePolygonSynchronizer();
    this.polygonTypeCalculator = new PolygonTypeCalculator();

    this.initializeImagePolygonPair();
  }

  /**
   * 이미지-폴리곤 쌍 초기화
   */
  async initializeImagePolygonPair() {
    try {
      await this.loadAndCreateImage();
      this.createAndAddPolygon();
      //폴리곤의 가로세로 위경도 크기 계산(초기 이미지의 크기 결정)
      this.setupImagePolygonSynchronization();

      // ✅ 폴리곤 생성 후 폴리곤 기반 이미지 동기화
      this.calculateAndApplyOptimalScale();

      this.isLoaded = true;
      log.complete(`ImagePolygonPair-${this.id}`, '초기화');
    } catch (error) {
      log.error(`ImagePolygonPair-${this.id}`, '초기화', error);
    }
  }

  /**
   * 이미지 로딩 및 GeoImage 생성
   */
  async loadAndCreateImage() {
    // 이미지 로딩
    this.originalImage = await this.imageLoader.loadImageFromUrl(this.imageUrl);

    // GeoImage 생성
    this.createGeoImageLayer();

  }

  /**
   * GeoImage 레이어 생성
   */
  createGeoImageLayer() {
    log.start(`ImagePolygonPair-${this.id}`, 'GeoImageLayer 생성');

    // 중심점 계산
    const center = this.geometryCalculator.calculatePolygonCenterFromCoordinates(this.coordinates);

    // GeoImageSource 생성
    this.geoImageSource = this.geoImageCreator.createGeoImageSource(
      this.imageUrl,
      center,
      [1, 1], // 초기 스케일
      0       // 초기 회전
    );

    // GeoImageLayer 생성
    this.geoImageLayer = this.geoImageCreator.createGeoImageLayer(
      this.geoImageSource,
      {
        name: `GeoImage-${this.id}`,
        opacity: this.options.opacity,
        zIndex: 100
      }
    );

    // 맵에 레이어 추가
    this.openLayersMap.addLayer(this.geoImageLayer);

    log.complete(`ImagePolygonPair-${this.id}`, 'GeoImageLayer 생성');
  }

  /**
   * 폴리곤 기반 이미지 동기화 (초기 설정)
   */
  calculateAndApplyOptimalScale() {
    if (!this.geoImageSource || !this.originalImage || !this.currentPolygon) return;

    // ✅ 폴리곤 기반으로 이미지 동기화 (interaction과 동일한 방식)
    const polygonGeometry = this.currentPolygon.getGeometry();
    this.synchronizeImageWithPolygon(polygonGeometry);

    log.info(`ImagePolygonPair-${this.id}`, '폴리곤 기반 초기 이미지 동기화 완료');
  }

  /**
   * 폴리곤 생성 및 추가
   */
  createAndAddPolygon() {
    // 폴리곤 피처 생성
    this.currentPolygon = this.polygonCreator.createSimplePolygonFeature(this.coordinates, this.id);

    if (!this.currentPolygon) {
      throw new Error(`폴리곤 생성 실패 - ID: ${this.id}`);
    }
    // 공유 벡터 소스에 추가
    this.sharedVectorSource.addFeature(this.currentPolygon);
    this.originalPolygonGeometry = this.currentPolygon.getGeometry().clone();

    log.complete(`ImagePolygonPair-${this.id}`, '폴리곤 생성');
  }

  /**
   * 이미지-폴리곤 동기화 설정
   */
  setupImagePolygonSynchronization() {
    // 원본 폴리곤 크기 정보 저장 (단위 통일)
    if (!this.originalPolygonSize) {
      // 실제 폴리곤 좌표 가져오기
      const coordinates = this.originalPolygonGeometry.getCoordinates()[0];

      // 새로운 PolygonTypeCalculator를 사용하여 정확한 크기 계산
      const polygonInfo = this.polygonTypeCalculator.calculateAccuratePolygonSize(coordinates);

      this.originalPolygonSize = {
        width: polygonInfo.width,   // 정확한 너비
        height: polygonInfo.height, // 정확한 높이
        type: polygonInfo.type      // 사각형 종류 저장
      };

      console.log('[ImagePolygonPair] 정확한 폴리곤 크기 계산:', {
        coordinates: coordinates.length,
        polygonType: polygonInfo.type,
        polygonSize: { width: polygonInfo.width, height: polygonInfo.height },
        originalPolygonSize: this.originalPolygonSize
      });
    }
  }

  /**
   * 이미지와 폴리곤 동기화 (공개 메서드)
   */
  synchronizeImageWithPolygon(geometry) {
    try {
      const imageSize = this.imageLoader.getImageSizeInfo(this.originalImage);

      console.log(`ImagePolygonPair ${this.id}: 동기화 시작`, {
        imageSize: imageSize,
        originalPolygonSize: this.originalPolygonSize,
        geometryType: geometry.getType()
      });

      // 새로운 유틸리티를 사용하여 GeoImage 속성 계산
      const geoImageProperties = imageCoordinateCalculator.calculateGeoImageProperties(
        geometry,
        this.originalPolygonGeometry,
        this.originalPolygonSize,
        imageSize
      );

      if (geoImageProperties) {
        // GeoImage 업데이트
        this.geoImageSource.setCenter(geoImageProperties.center);
        this.geoImageSource.setScale(geoImageProperties.scale);
        this.geoImageSource.setRotation(geoImageProperties.rotation);

        console.log(`ImagePolygonPair ${this.id}: 동기화 성공`, {
          center: geoImageProperties.center,
          scale: geoImageProperties.scale,
          rotation: geoImageProperties.rotation * 180 / Math.PI
        });

        // 강제 렌더링
        this.openLayersMap.render();
      } else {
        console.warn(`ImagePolygonPair ${this.id}: 동기화 실패 - 속성 계산 실패`);
      }
    } catch (error) {
      console.error(`ImagePolygonPair ${this.id}: 동기화 중 오류`, error);
    }
  }


  /**
   * 이미지에서 폴리곤 업데이트
   */
  updatePolygonFromImage() {
    if (!this.geoImageSource || !this.currentPolygon) return;

    this.imagePolygonSynchronizer.updatePolygonFromImage(this.geoImageSource, this.currentPolygon);
  }

  /**
   * 정보 가져오기
   */
  getInfo() {
    if (!this.currentPolygon || !this.geoImageSource) return null;

    const geometry = this.currentPolygon.getGeometry();
    const extent = geometry.getExtent();
    const center = getCenter(extent);
    const scale = this.geoImageSource.getScale();
    const rotation = this.geoImageSource.getRotation();

    return {
      id: this.id,
      center: center,
      extent: extent,
      coordinates: geometry.getCoordinates(),
      rotation: rotation * 180 / Math.PI,
      scale: scale,
      isVisible: this.isVisible,
      isLoaded: this.isLoaded
    };
  }

  /**
   * 삭제
   */
  destroy() {
    // GeoImage 레이어 제거
    if (this.geoImageLayer) {
      this.openLayersMap.removeLayer(this.geoImageLayer);
    }

    // 공유 벡터 소스에서 현재 폴리곤 Feature 제거
    if (this.currentPolygon && this.sharedVectorSource) {
      this.sharedVectorSource.removeFeature(this.currentPolygon);
    }

    console.log(`ImagePolygonPair ${this.id}: 삭제 완료`);
  }
}

/**
 * 이미지와 폴리곤을 한 쌍으로 관리하는 매니저 클래스 (리팩토링됨)
 */
export class ImagePolygonMapManager {
  constructor(openLayersMap) {
    log.start('ImagePolygonMapManager', '초기화');

    if (!openLayersMap) {
      throw new Error('openLayersMap parameter is required');
    }

    this.openLayersMap = openLayersMap; // OpenLayers Map 객체

    // Canvas 성능 최적화 설정
    this.optimizeCanvasPerformance();
    // Vue 반응형 시스템 문제를 피하기 위해 일반 객체 사용
    this.imagePolygonPairs = {};

    // 기존 유틸리티 클래스들 초기화
    this.styleManager = new StyleManager();
    this.fixedCenterModificationHandler = new FixedCenterModificationHandler();
    this.interactionSetupManager = new InteractionSetupManager();
    this.polygonTypeCalculator = new PolygonTypeCalculator();

    // 새로운 핸들러들 초기화
    this.transformationTracker = new TransformationTracker();
    this.transformationApi = new TransformationApi(); // Store 직접 접근
    this.transformationCalculator = new TransformationCalculator();
    this.uiHandler = new TransformationUIHandler(() => this.imagePolygonPairs); // 함수로 참조 전달을 하여 opacity설정이 되도록함
    this.modifyHandler = new ModifyHandler(this.transformationTracker, this.transformationApi);
    this.translateHandler = new TranslateHandler(this.transformationTracker, this.transformationApi);

    // 다중 선택 관리 (Collection 사용)
    this.selectedPolygons = new Collection();
    this.selectionSource = new VectorSource();
    this.selectionLayer = new VectorLayer({
      source: this.selectionSource,
      style: this.styleManager.createSelectionStyle(),
      zIndex: 1001
    });

    // 공유 벡터 소스와 레이어 설정
    this.sharedVectorSource = new VectorSource();
    this.sharedVectorLayer = new VectorLayer({
      source: this.sharedVectorSource,
      style: this.styleManager.createDefaultPolygonStyle(),
      zIndex: 1000
    });

    // 맵에 공유 레이어 추가
    this.openLayersMap.addLayer(this.sharedVectorLayer);

    // 선택 레이어 추가
    this.openLayersMap.addLayer(this.selectionLayer);

    // Context Menu 초기화
    this.initializeContextMenu();

    // 통합 인터랙션 설정
    this.setupUnifiedInteractions();

    log.complete('ImagePolygonMapManager', '초기화');
  }

  /**
   * Canvas 성능 최적화 설정
   * willReadFrequently 속성을 설정하여 getImageData() 성능 향상
   */
  optimizeCanvasPerformance() {
    try {
      // OpenLayers 맵의 Canvas 요소들에 willReadFrequently 설정
      const mapElement = this.openLayersMap.getTargetElement();
      const canvasElements = mapElement.querySelectorAll('canvas');

      canvasElements.forEach(canvas => {
        if (canvas.getContext) {
          // 기존 context가 있다면 새로운 context로 교체
          const ctx = canvas.getContext('2d', { willReadFrequently: true });
          if (ctx) {
            log.info('ImagePolygonMapManager', 'Canvas 성능 최적화 적용됨', {
              canvasId: canvas.id || 'unnamed',
              willReadFrequently: true
            });
          }
        }
      });

      // 맵 렌더링 완료 후 추가 Canvas 요소들도 최적화
      this.openLayersMap.once('rendercomplete', () => {
        const additionalCanvasElements = mapElement.querySelectorAll('canvas');
        additionalCanvasElements.forEach(canvas => {
          if (canvas.getContext && !canvas._optimized) {
            const ctx = canvas.getContext('2d', { willReadFrequently: true });
            if (ctx) {
              canvas._optimized = true;
              log.info('ImagePolygonMapManager', '추가 Canvas 성능 최적화 적용됨', {
                canvasId: canvas.id || 'unnamed'
              });
            }
          }
        });
      });

      log.info('ImagePolygonMapManager', 'Canvas 성능 최적화 설정 완료');
    } catch (error) {
      log.error('ImagePolygonMapManager', 'Canvas 성능 최적화 설정 실패', error);
    }
  }

  /**
   * 폴리곤 중심점 계산
   */
  calculatePolygonCenter(geometry) {
    const centerInfo = imageCoordinateCalculator.calculateGeometryCenter(geometry);
    return centerInfo ? centerInfo.center : null;
  }

  /**
   * 최소 반지름 계산 (평행사변형 반지름 계산 함수로 대체됨)
   * @deprecated 평행사변형 반지름 계산 함수 사용 권장
   */
  // calculateMinRadius(geometry, center) {
  //   return imageCoordinateCalculator.calculateMinRadius(geometry, center);
  // }

  /**
   * Context Menu 초기화
   */
  initializeContextMenu() {
    log.start('ImagePolygonMapManager', 'Context Menu 초기화');

    try {
      this.contextMenu = new ContextMenu({
        width: 250,
        defaultItems: false, // 기본 줌 인/아웃 아이템 제거
        items: [
          {
            text: i18n.t('imagePolygonMap.contextClearRectSelection'),
            icon: 'mdi-close-circle',
            callback: () => {
              this.clearSelection();
              log.info('ImagePolygonMapManager', 'Context Menu: 전체 선택 해제');
            }
          },
          '-', // 구분선
          {
            text: i18n.t('imagePolygonMap.contextSelectedRectInfo'),
            icon: 'mdi-information',
            callback: () => {
              // Context Menu에 정보 표시 (실제로는 아무것도 하지 않음, 정보는 이미 표시됨)
            }
          }
        ]
      });

      // Context Menu 열릴 때 동적으로 아이템 업데이트
      this.contextMenu.on('beforeopen', (event) => {
        this.updateContextMenuItems(event);
      });

      // 맵에 Context Menu 추가
      this.openLayersMap.addControl(this.contextMenu);

      log.complete('ImagePolygonMapManager', 'Context Menu 초기화');
    } catch (error) {
      log.error('ImagePolygonMapManager', 'Context Menu 초기화 실패', error);
    }
  }

  /**
   * Context Menu 아이템 동적 업데이트
   */
  updateContextMenuItems(event) {
    const selectedCount = this.selectedPolygons.getLength();
    const selectedIds = this.selectedPolygons.getArray().map(f => f.get('imageId'));

    // 기존 아이템 제거
    this.contextMenu.clear();

    // 기본 아이템들 추가
    const baseItems = [
      {
        text: i18n.t('imagePolygonMap.contextClearAllSelection'),
        icon: 'mdi-close-circle',
        callback: () => {
          this.clearSelection();
          log.info('ImagePolygonMapManager', 'Context Menu: 전체 선택 해제');
        }
      }
    ];

    // 선택된 폴리곤이 있는 경우에만 정보 표시
    if (selectedCount > 0) {
      baseItems.push('-'); // 구분선
      baseItems.push({
        text: i18n.t('imagePolygonMap.selectedPolygonCount', { count: selectedCount }),
        icon: 'mdi-check-circle',
        callback: () => {} // 클릭해도 아무것도 하지 않음
      });

      // 선택된 폴리곤 ID들 표시 (최대 5개까지만)
      const displayIds = selectedIds.slice(0, 5);
      displayIds.forEach(id => {
        baseItems.push({
          text: `ID: ${id}`,
          icon: 'mdi-tag',
          callback: () => {} // 클릭해도 아무것도 하지 않음
        });
      });

      // 5개 이상인 경우 추가 표시
      if (selectedIds.length > 5) {
        baseItems.push({
          text: i18n.t('imagePolygonMap.moreItems', { count: selectedIds.length - 5 }),
          icon: 'mdi-dots-horizontal',
          callback: () => {} // 클릭해도 아무것도 하지 않음
        });
      }
    } else {
      // 선택된 폴리곤이 없는 경우
      baseItems.push('-'); // 구분선
      baseItems.push({
        text: i18n.t('imagePolygonMap.selectedPolygonNone'),
        icon: 'mdi-information',
        callback: () => {} // 클릭해도 아무것도 하지 않음
      });
    }

    // 아이템들을 Context Menu에 추가
    this.contextMenu.extend(baseItems);

    log.info('ImagePolygonMapManager', 'Context Menu 업데이트', {
      selectedCount: selectedCount,
      selectedIds: selectedIds
    });
  }

  /**
   * 다중 선택 인터랙션 설정
   */
  setupMultiSelectionInteraction() {
    log.start('ImagePolygonMapManager', '다중 선택 인터랙션 설정');

    // Select 인터랙션 생성 (하이브리드 방식)
    this.multiSelectInteraction = new Select({
      source: this.sharedVectorSource,
      toggleCondition: (event) => {
        return primaryAction(event);
      },
      multi: false, // 다중 선택 활성화
      style: this.styleManager.createSelectionStyle()
    });

    // 선택 이벤트 처리
    this.multiSelectInteraction.on('select', (event) => {
      this.handleHybridSelectionChange(event);
    });

    // 맵에 다중 선택 인터랙션 추가
    this.openLayersMap.addInteraction(this.multiSelectInteraction);

    log.complete('ImagePolygonMapManager', '다중 선택 인터랙션 설정');
  }

  /**
   * 하이브리드 선택 변경 이벤트 처리
   */
  handleHybridSelectionChange(event) {
    log.start('ImagePolygonMapManager', '하이브리드 선택 변경 처리');

    // 새로 선택된 폴리곤들 처리 (토글 방식)
    const selectedFeatures = event.selected;
    selectedFeatures.forEach(feature => {
      const imageId = feature?.get('imageId') || null;
      if(imageId){
        // Collection에 feature 추가
        if(!this.selectedPolygons.getArray().some(f => f.get('imageId') === imageId)) {
          this.selectedPolygons.push(feature);
        }
      }
    });

    const deselectedFeatures = event.deselected;
    deselectedFeatures.forEach(feature => {
      const imageId = feature?.get('imageId') || null;
      if(imageId){
        // Collection에서 feature 제거
        const index = this.selectedPolygons.getArray().findIndex(f => f.get('imageId') === imageId);
        if(index !== -1) {
          this.selectedPolygons.removeAt(index);
        }
      }
    });

    log.info('ImagePolygonMapManager', '선택된 폴리곤', {
      selectedCount: this.selectedPolygons.getLength(),
      selectedIds: this.selectedPolygons.getArray().map(f => f.get('imageId'))
    });
    log.complete('ImagePolygonMapManager', '하이브리드 선택 변경 처리');
  }

  /**
   * 선택된 모든 폴리곤의 중심점 계산
   */
  calculateSelectedPolygonsCenter() {
    if (this.selectedPolygons.getLength() === 0) return null;

    let totalX = 0, totalY = 0;
    let count = 0;

    this.selectedPolygons.getArray().forEach(feature => {
      const imageId = feature.get('imageId');
      const imagePolygonPair = this.imagePolygonPairs[imageId];
      if (imagePolygonPair && imagePolygonPair.currentPolygon) {
        const center = this.calculatePolygonCenter(imagePolygonPair.currentPolygon.getGeometry());
        totalX += center[0];
        totalY += center[1];
        count++;
      }
    });

    return count > 0 ? [totalX / count, totalY / count] : null;
  }

  /**
   * 선택된 모든 폴리곤과 이미지 동시 회전
   */
  rotateSelectedPolygons(rotationAngle, center) {
    if (this.selectedPolygons.getLength() === 0) return;

    log.start('ImagePolygonMapManager', '선택된 폴리곤들 동시 회전', {
      count: this.selectedPolygons.getLength(),
      angle: rotationAngle * 180 / Math.PI
    });

    this.selectedPolygons.getArray().forEach(feature => {
      const imageId = feature.get('imageId');
      const imagePolygonPair = this.imagePolygonPairs[imageId];
      if (imagePolygonPair && imagePolygonPair.currentPolygon) {
        const geometry = imagePolygonPair.currentPolygon.getGeometry();
        const rotatedGeometry = geometry.clone();
        rotatedGeometry.rotate(rotationAngle, center);

        // 폴리곤 geometry 업데이트
        imagePolygonPair.currentPolygon.setGeometry(rotatedGeometry);

        // 이미지 동기화
        imagePolygonPair.synchronizeImageWithPolygon(rotatedGeometry);

        log.info('ImagePolygonMapManager', '폴리곤 회전 완료', { id: imageId });
      }
    });

    log.complete('ImagePolygonMapManager', '선택된 폴리곤들 동시 회전');
  }

  /**
   * Modify 인터랙션의 스타일 함수
   */
  createModifyStyleFunction(defaultStyle) {
    return (feature, resolution) => {
      const features = this.selectedPolygons.getArray();
      let modifiedGeometry = null;

      features.forEach((modifyFeature) => {
        const imageId = modifyFeature.get('imageId');
        const imagePolygonPair = this.imagePolygonPairs[imageId];

        if (!imagePolygonPair) {
          log.warn('ImagePolygonMapManager', 'ImagePolygonPair를 찾을 수 없음', { id: imageId });
          return;
        }

        const modifyGeometry = modifyFeature.get('modifyGeometry');
        if (modifyGeometry) {
          modifiedGeometry = this.processModifyGeometry(feature, modifyFeature, modifyGeometry, imagePolygonPair);
        }
      });

      // 폴리곤을 직접 렌더링 (interaction 중 연한 색상)
      if (modifiedGeometry) {
        const surfaceColor = getThemeVar('--v-surface-base', '#FFFFFF');
        const errorColor = getThemeVar('--v-error-base', '#C62828');
        return new Style({
          geometry: modifiedGeometry,
          fill: new Fill({ color: withOpacity(surfaceColor, 0.08) }),
          stroke: new Stroke({
            color: withOpacity(errorColor, 0.6),
            width: 2,
            lineDash: [5, 10]
          })
        });
      }

      return defaultStyle(feature, resolution);
    };
  }

  /**
   * Modify geometry 처리
   */
  processModifyGeometry(feature, modifyFeature, modifyGeometry, imagePolygonPair) {
    const point = feature.getGeometry().getCoordinates();
    const modifyPoint = modifyGeometry.point;

    if (!modifyPoint) {
      this.initializeModifyGeometry(modifyGeometry, point, modifyFeature.get('imageId'));
    }

    const center = modifyGeometry.center;
    const minRadius = modifyGeometry.minRadius;

         return this.calculateAndApplyTransformation(
       point,
       modifyPoint,
       center,
       minRadius,
       modifyGeometry,
       imagePolygonPair,
       modifyFeature
     );
  }

  /**
   * Modify geometry 초기화
   */
  initializeModifyGeometry(modifyGeometry, point, imageId) {
    // 초기 geometry와 vertex 위치 저장
    modifyGeometry.point = point;
    modifyGeometry.geometry0 = modifyGeometry.geometry;

    // 중심점 계산
    const center = this.calculatePolygonCenter(modifyGeometry.geometry0);

    // 평행사변형 반지름 계산 함수를 사용하여 최소 반지름 계산
    const radiusInfo = imageCoordinateCalculator.calculateParallelogramRadius(point, center, 0);
    const minRadius = radiusInfo.radius * 0.1; // 최소 반지름을 초기 반지름의 10%로 설정

    modifyGeometry.center = center;
    modifyGeometry.minRadius = minRadius;

    log.info('ImagePolygonMapManager', '중심점 고정 인터랙션 초기 설정', {
      id: imageId,
      center: center,
      minRadius: minRadius,
      initialRadius: radiusInfo.radius
    });
  }

  /**
   * 변형 계산 및 적용
   */
  calculateAndApplyTransformation(point, modifyPoint, center, minRadius, modifyGeometry, imagePolygonPair, modifyFeature) {
    // 유틸리티를 사용하여 변형 계산
    const transformation = imageCoordinateCalculator.calculateModifyTransformation(
      point, modifyPoint, center, minRadius
    );

    if (!transformation) {
      return null;
    }

    const { scaleRatio, rotationAngle } = transformation;

    log.info('ImagePolygonMapManager', '폴리곤 변형 계산', {
      id: imagePolygonPair.id,
      scaleRatio: scaleRatio,
      rotationAngle: rotationAngle * 180 / Math.PI
    });

    // 변형 적용
    const geometry = imageCoordinateCalculator.applyTransformationToGeometry(
      modifyGeometry.geometry0, scaleRatio, rotationAngle, center
    );

    modifyGeometry.geometry = geometry;

    // 현재 조작 중인 feature의 geometry 업데이트
    modifyFeature.setGeometry(geometry);

    // currentPolygon 업데이트
    if (imagePolygonPair.currentPolygon) {
      imagePolygonPair.currentPolygon.setGeometry(geometry);
    }

    // 이미지 동기화
    imagePolygonPair.synchronizeImageWithPolygon(geometry);

    // 다른 선택된 폴리곤들 동기화
    this.synchronizeOtherSelectedPolygons(scaleRatio, rotationAngle);

    log.info('ImagePolygonMapManager', 'style 함수에서 geometry 계산 및 동기화 완료', {
      id: imagePolygonPair.id,
      scaleRatio: scaleRatio.toFixed(3),
      rotationAngle: (rotationAngle * 180 / Math.PI) + '°',
      selectedCount: this.selectedPolygons.getLength()
    });

    return geometry;
  }

  /**
   * Geometry에 변형 적용
   */
  applyTransformationToGeometry(geometry, scaleRatio, rotationAngle, center) {
    return imageCoordinateCalculator.applyTransformationToGeometry(
      geometry, scaleRatio, rotationAngle, center
    );
  }

  /**
   * 다른 선택된 폴리곤들 동기화 (각 폴리곤의 center 유지)
   */
  synchronizeOtherSelectedPolygons(scaleRatio, rotationAngle) {
    if (this.selectedPolygons.getLength() > 1) {
      const selectedFeatures = this.selectedPolygons.getArray();

      selectedFeatures.forEach((selectedFeature) => {
        const selectedImageId = selectedFeature.get('imageId');
        const selectedImagePolygonPair = this.imagePolygonPairs[selectedImageId];

        if (selectedImagePolygonPair && selectedImagePolygonPair.currentPolygon) {
          const selectedModifyGeometry = selectedFeature.get('modifyGeometry');
          if (selectedModifyGeometry && selectedModifyGeometry.geometry0) {
            // 각 폴리곤의 개별 center 사용
            const selectedCenter = selectedModifyGeometry.center;
            if (selectedCenter) {
                          // 각 폴리곤의 개별 center를 기준으로 변형 적용
            const selectedGeometry = imageCoordinateCalculator.applyTransformationToGeometry(
              selectedModifyGeometry.geometry0, scaleRatio, rotationAngle, selectedCenter
            );
              selectedModifyGeometry.geometry = selectedGeometry;

              // feature의 geometry 업데이트
              selectedFeature.setGeometry(selectedGeometry);

              // currentPolygon 업데이트
              if (selectedImagePolygonPair.currentPolygon) {
                selectedImagePolygonPair.currentPolygon.setGeometry(selectedGeometry);
              }

              // 이미지 동기화
              selectedImagePolygonPair.synchronizeImageWithPolygon(selectedGeometry);

              log.info('ImagePolygonMapManager', '다른 폴리곤 동기화 (개별 center 유지)', {
                id: selectedImageId,
                scaleRatio: scaleRatio.toFixed(3),
                rotationAngle: (rotationAngle * 180 / Math.PI).toFixed(1) + '°',
                center: selectedCenter
              });
            }
          }
        }
      });
    }
  }

  /**
   * Modify 시작 이벤트 핸들러 (새로운 구조)
   */
  handleModifyStart(event) {
    log.start('ImagePolygonMapManager', 'Modify 시작');
    const features = this.selectedPolygons.getArray();
    // UI 처리
    features.forEach(feature => {
      // const imageId = feature.get('imageId');
      // const imagePolygonPair = this.imagePolygonPairs[imageId];
      // console.log('imagePolygonPair && imagePolygonPair.geoImageLayer)',imagePolygonPair, imagePolygonPair?.geoImageLayer);
      this.uiHandler.initializeModifyUI(feature);
      this.initializeFeatureForModify(feature);
    });

    // 데이터 처리
    this.modifyHandler.handleModifyStart(event);
  }

  /**
   * Modify 완료 이벤트 핸들러 (새로운 구조)
   */
  async handleModifyEnd(event) {
    const features = this.selectedPolygons.getArray();
    log.complete('ImagePolygonMapManager', 'Modify 완료');

    // 데이터 처리 및 서버 전송은 핸들러에서
    const result = await this.modifyHandler.handleModifyEnd(event);

    if (result.success) {
      // 성공 시 데이터 처리 먼저
      features.forEach(feature => {
        this.finalizeEachFeatureModify(feature);
      });

      // 그 다음 UI 처리
      features.forEach(feature => {
        this.uiHandler.finalizeModifyUI(feature);
      });
    }
    // 실패 시 에러 핸들러가 자동으로 처리
  }

  /**
   * Feature의 Modify 초기화 (데이터 관련만 처리)
   */
  initializeFeatureForModify(feature) {
    const imageId = feature.get('imageId');
    const imagePolygonPair = this.imagePolygonPairs[imageId];

    log.info('ImagePolygonMapManager', 'Modify 시작', { id: imageId });

    // modifyGeometry 초기화
    const modifyGeometry = {
      geometry: feature.getGeometry().clone(),
      geometry0: feature.getGeometry().clone(),
      point: null,
      center: null,
      minRadius: null
    };

    feature.set('modifyGeometry', modifyGeometry);

    log.info('ImagePolygonMapManager', 'modifyGeometry 초기화', {
      id: imageId,
      geometry: modifyGeometry.geometry.getCoordinates()
    });
  }

  /**
   * 각 Feature의 Modify 완료 처리 (데이터 관련만 처리)
   */
  finalizeEachFeatureModify(feature) {
    const imageId = feature.get('imageId');
    const imagePolygonPair = this.imagePolygonPairs[imageId];

    if (!imagePolygonPair) {
      log.warn('ImagePolygonMapManager', 'ImagePolygonPair를 찾을 수 없음', { id: imageId });
      return;
    }

    log.info('ImagePolygonMapManager', 'Modify 완료', { id: imageId });

    const modifyGeometry = feature.get('modifyGeometry');
    if (modifyGeometry) {
      log.info('ImagePolygonMapManager', 'modifyGeometry 존재', {
        id: imageId,
        hasGeometry: !!modifyGeometry.geometry
      });

      if (modifyGeometry.geometry) {
        log.info('ImagePolygonMapManager', '최종 geometry 좌표', {
          id: imageId,
          coordinates: modifyGeometry.geometry.getCoordinates()
        });

        // 변형된 geometry를 실제 feature에 적용
        feature.setGeometry(modifyGeometry.geometry);

        // 최종 이미지 동기화
        imagePolygonPair.synchronizeImageWithPolygon(modifyGeometry.geometry);

        log.info('ImagePolygonMapManager', '최종 geometry 적용 및 동기화 완료', { id: imageId,
          coordinates: modifyGeometry.geometry.getCoordinates(),
          center: modifyGeometry.center
         });
      }

      // modifyGeometry 정리
      feature.unset('modifyGeometry', true);
    } else {
      log.warn('ImagePolygonMapManager', 'modifyGeometry가 없음 - 기본 geometry 사용', { id: imageId });
    }
  }

  /**
   * 각 Feature의 Translate 완료 처리 (데이터 관련만 처리)
   */
  finalizeEachFeatureTranslate(feature) {
    const imageId = feature.get('imageId');
    const imagePolygonPair = this.imagePolygonPairs[imageId];

    if (!imagePolygonPair) {
      log.warn('ImagePolygonMapManager', 'ImagePolygonPair를 찾을 수 없음', { id: imageId });
      return;
    }

    log.info('ImagePolygonMapManager', 'Translate 완료', { id: imageId });

    // 최종 이미지 동기화
    const geometry = feature.getGeometry();
    if (geometry) {
      log.info('ImagePolygonMapManager', '최종 geometry 좌표', {
        id: imageId,
        coordinates: geometry.getCoordinates()
      });

      // 최종 이미지 동기화
      imagePolygonPair.synchronizeImageWithPolygon(geometry);

      log.info('ImagePolygonMapManager', '최종 geometry 적용 및 동기화 완료', { id: imageId,
        coordinates: geometry.getCoordinates()
       });
    }
  }

  /**
   * 통합 인터랙션 설정
   */
  setupUnifiedInteractions() {
    log.start('ImagePolygonMapManager', '통합 인터랙션 설정');

    try {
      // 다중 선택 인터랙션 설정
      this.setupMultiSelectionInteraction();

      // 기본 스타일 함수 가져오기
      const defaultStyle = this.getDefaultModifyStyle();

      // Modify 인터랙션 생성
      this.sharedModifyInteraction = new Modify({
        features: this.selectedPolygons,
        condition: function (event) {
          return primaryAction(event) && !platformModifierKeyOnly(event);
        },
        deleteCondition: never,
        insertVertexCondition: never,
        style: this.createModifyStyleFunction(defaultStyle)
      });

      // Modify 이벤트 핸들러 설정
      this.sharedModifyInteraction.on('modifystart', (event) => {
        this.handleModifyStart(event);
      });

      this.sharedModifyInteraction.on('modifyend', async (event) => {
        this.handleModifyEnd(event);
      });

      // Translate 인터랙션 설정
      this.setupTranslateInteraction();

      // 맵에 인터랙션 추가
      this.openLayersMap.addInteraction(this.sharedModifyInteraction);
      this.openLayersMap.addInteraction(this.sharedTranslateInteraction);

      log.complete('ImagePolygonMapManager', '통합 인터랙션 설정');
    } catch (error) {
      log.error('ImagePolygonMapManager', '통합 인터랙션 설정', error);
      throw error;
    }
  }

  /**
   * 기본 Modify 스타일 가져오기
   */
  getDefaultModifyStyle() {
    try {
      const modifyInteraction = new Modify({features: this.selectedPolygons});
      const overlay = modifyInteraction.getOverlay();
      if (overlay && typeof overlay.getStyleFunction === 'function') {
        return overlay.getStyleFunction();
      }
    } catch (styleError) {
      log.warn('ImagePolygonMapManager', '기본 스타일 함수 가져오기 실패', { error: styleError.message });
    }

    // 기본 스타일 함수를 직접 생성
    return this.styleManager.createDefaultPolygonStyle();
  }

  /**
   * Translate 인터랙션 설정
   */
  setupTranslateInteraction() {
    // 이동 인터랙션 설정 (Collection 사용)
    this.sharedTranslateInteraction = new Translate({
      condition: function (event) {
        return primaryAction(event) && platformModifierKeyOnly(event);
      },
      features: this.selectedPolygons,
      pixelTolerance: 10
    });

    // Translate 시작 이벤트
    this.sharedTranslateInteraction.on('translatestart', (event) => {
      this.handleTranslateStart(event);
    });

    // Translate 완료 이벤트
    this.sharedTranslateInteraction.on('translateend', (event) => {
      this.handleTranslateEnd(event);
    });
  }

  /**
   * Translate 시작 이벤트 핸들러 (새로운 구조)
   */
  handleTranslateStart(event) {
    log.start('ImagePolygonMapManager', 'Translate 시작');

    // UI 처리
    event.features.forEach(feature => {
      this.uiHandler.initializeTranslateUI(feature);
    });

    // 데이터 처리는 핸들러로 위임
    this.translateHandler.handleTranslateStart(event);
  }

  /**
   * Translate 완료 이벤트 핸들러 (새로운 구조)
   */
  async handleTranslateEnd(event) {
    log.complete('ImagePolygonMapManager', 'Translate 완료');

    // 데이터 처리 및 서버 전송은 핸들러에서
    const result = await this.translateHandler.handleTranslateEnd(event);

    if (result.success) {
      // 성공 시 데이터 처리 먼저
      event.features.forEach(feature => {
        this.finalizeEachFeatureTranslate(feature);
      });

      // 그 다음 UI 처리
      event.features.forEach(feature => {
        this.uiHandler.finalizeTranslateUI(feature);
      });
    }
    // 실패 시 에러 핸들러가 자동으로 처리
  }

  /**
   * 이미지 데이터로 이미지-폴리곤 쌍 생성
   */
  createImagePolygonPairs(imageDataArray) {
    log.start('ImagePolygonMapManager', '이미지-폴리곤 쌍 생성', { count: imageDataArray.length });

    // 기존 이미지-폴리곤 쌍 제거
    this.clearAll();

    imageDataArray.forEach((imageData, index) => {
      log.progress('ImagePolygonMapManager', '이미지-폴리곤 쌍 처리', index + 1, imageDataArray.length, {
        id: imageData.id,
        name: imageData.name
      });

      try {
        this.createImagePolygonPair(imageData);
      } catch (error) {
        log.error('ImagePolygonMapManager', '이미지-폴리곤 쌍 생성', error, { id: imageData.id });
      }
    });

    log.complete('ImagePolygonMapManager', '이미지-폴리곤 쌍 생성');
  }

  /**
   * 이미지-폴리곤 쌍 생성
   */
  async createImagePolygonPair(imageData) {
    try {
      const { id, thumbImgUrl, imgName } = imageData;
      const coordinates = imageData?.imgPosCoords?.coordinates[0];

      log.start('ImagePolygonMapManager', '개별 이미지-폴리곤 쌍 생성', { id });

      const imagePolygonPair = new ImagePolygonPair(
        id,
        thumbImgUrl,
        this.openLayersMap,
        this.sharedVectorSource,
        coordinates,
        {
          opacity: 1.0
        }
      );

      this.imagePolygonPairs[id] = imagePolygonPair;

      log.complete('ImagePolygonMapManager', '개별 이미지-폴리곤 쌍 생성', {
        id,
        totalPairs: Object.keys(this.imagePolygonPairs).length
      });

    } catch (error) {
      log.error('ImagePolygonMapManager', '이미지-폴리곤 쌍 생성', error, { id: imageData.id });
      throw error;
    }
  }

  /**
   * 모든 폴리곤 제거
   */
  clearAll() {
    log.start('ImagePolygonMapManager', '모든 이미지-폴리곤 쌍 제거');

    try {
      // imagePolygonPairs가 존재하는지 확인
      if (!this.imagePolygonPairs) {
        log.warn('ImagePolygonMapManager', 'imagePolygonPairs가 초기화되지 않았습니다. 새로 생성합니다.');
        this.imagePolygonPairs = {};
        return;
      }

      // 일반 객체인지 확인
      if (typeof this.imagePolygonPairs !== 'object' || Array.isArray(this.imagePolygonPairs)) {
        log.warn('ImagePolygonMapManager', 'imagePolygonPairs가 일반 객체가 아닙니다', { type: typeof this.imagePolygonPairs });
        this.imagePolygonPairs = {};
        return;
      }

      // 기존 이미지-폴리곤 쌍들 정리
      try {
        const pairsToDestroy = Object.entries(this.imagePolygonPairs);
        pairsToDestroy.forEach(([id, imagePolygonPair]) => {
          try {
            if (imagePolygonPair && typeof imagePolygonPair.destroy === 'function') {
              imagePolygonPair.destroy();
            }
          } catch (error) {
            log.error('ImagePolygonMapManager', '이미지-폴리곤 쌍 제거', error, { id });
          }
        });
      } catch (entriesError) {
        log.error('ImagePolygonMapManager', 'Object.entries() 호출 실패', entriesError, {
          type: typeof this.imagePolygonPairs,
          constructor: this.imagePolygonPairs?.constructor?.name
        });
        // entries() 실패 시 안전하게 초기화
        this.imagePolygonPairs = {};
        return;
      }

      // 객체 초기화
      this.imagePolygonPairs = {};

      // 선택된 폴리곤들도 초기화
      this.selectedPolygons.clear();

      log.complete('ImagePolygonMapManager', '모든 이미지-폴리곤 쌍 제거');

    } catch (error) {
      log.error('ImagePolygonMapManager', 'clearAll', error);
      // 오류 발생 시 안전하게 초기화
      this.imagePolygonPairs = {};
    }
  }

  /**
   * 특정 폴리곤 제거
   */
  removePolygon(id) {
    const imagePolygonPair = this.imagePolygonPairs[id];
    if (imagePolygonPair) {
      if (typeof imagePolygonPair.destroy === 'function') {
        imagePolygonPair.destroy();
      }
      delete this.imagePolygonPairs[id];
      log.info('ImagePolygonMapManager', '폴리곤 제거', { id });
    }
  }

  /**
   * 폴리곤 정보 가져오기
   */
  getPolygonInfo(id) {
    const imagePolygonPair = this.imagePolygonPairs[id];
    return imagePolygonPair && typeof imagePolygonPair.getInfo === 'function'
      ? imagePolygonPair.getInfo()
      : null;
  }

  /**
   * 모든 폴리곤 정보 가져오기
   */
  getAllPolygonInfo() {
    const info = {};

    try {
      if (!this.imagePolygonPairs || typeof this.imagePolygonPairs !== 'object' || Array.isArray(this.imagePolygonPairs)) {
        log.warn('ImagePolygonMapManager', 'imagePolygonPairs가 유효하지 않습니다', {
          type: typeof this.imagePolygonPairs,
          constructor: this.imagePolygonPairs?.constructor?.name
        });
        return info;
      }

      const pairs = Object.entries(this.imagePolygonPairs);
      pairs.forEach(([id, imagePolygonPair]) => {
        try {
          if (imagePolygonPair && typeof imagePolygonPair.getInfo === 'function') {
            info[id] = imagePolygonPair.getInfo();
          }
        } catch (error) {
          log.error('ImagePolygonMapManager', '이미지-폴리곤 쌍 정보 가져오기', error, { id });
        }
      });
    } catch (error) {
      log.error('ImagePolygonMapManager', 'getAllPolygonInfo', error, {
        imagePolygonPairsType: typeof this.imagePolygonPairs,
        imagePolygonPairsConstructor: this.imagePolygonPairs?.constructor?.name
      });
    }

    return info;
  }

  /**
   * 폴리곤 개수 가져오기
   */
  getPolygonCount() {
    try {
      if (!this.imagePolygonPairs || typeof this.imagePolygonPairs !== 'object' || Array.isArray(this.imagePolygonPairs)) {
        log.warn('ImagePolygonMapManager', 'imagePolygonPairs가 유효하지 않습니다', {
          type: typeof this.imagePolygonPairs,
          constructor: this.imagePolygonPairs?.constructor?.name
        });
        return 0;
      }

      return Object.keys(this.imagePolygonPairs).length;
    } catch (error) {
      log.error('ImagePolygonMapManager', 'getPolygonCount', error, {
        imagePolygonPairsType: typeof this.imagePolygonPairs,
        imagePolygonPairsConstructor: this.imagePolygonPairs?.constructor?.name
      });
      return 0;
    }
  }

  /**
   * 선택된 폴리곤들 가져오기
   */
  getSelectedPolygons() {
    return this.selectedPolygons.getArray().map(f => f.get('imageId'));
  }

  /**
   * 선택된 폴리곤 개수 가져오기
   */
  getSelectedPolygonCount() {
    return this.selectedPolygons.getLength();
  }

  /**
   * 모든 선택 해제
   */
  clearSelection() {
    this.selectedPolygons.clear();
    this.selectionSource.clear();
    if (this.multiSelectInteraction) {
      this.multiSelectInteraction.getFeatures().clear();
    }
    log.info('ImagePolygonMapManager', '모든 선택 해제');
  }

  /**
   * 정리
   */
  destroy() {
    log.start('ImagePolygonMapManager', '정리');

    // 모든 이미지-폴리곤 쌍 정리
    this.clearAll();

    // 공유 레이어와 인터랙션 제거
    if (this.sharedVectorLayer && this.openLayersMap) {
      this.openLayersMap.removeLayer(this.sharedVectorLayer);
    }
    if (this.selectionLayer && this.openLayersMap) {
      this.openLayersMap.removeLayer(this.selectionLayer);
    }
    if (this.sharedModifyInteraction && this.openLayersMap) {
      this.openLayersMap.removeInteraction(this.sharedModifyInteraction);
    }
    if (this.sharedTranslateInteraction && this.openLayersMap) {
      this.openLayersMap.removeInteraction(this.sharedTranslateInteraction);
    }
    if (this.multiSelectInteraction && this.openLayersMap) {
      this.openLayersMap.removeInteraction(this.multiSelectInteraction);
    }

    // Context Menu 정리
    if (this.contextMenu && this.openLayersMap) {
      this.openLayersMap.removeControl(this.contextMenu);
    }

    log.complete('ImagePolygonMapManager', '정리');
  }
}
